import sendRequest from "src/helpers/service";
import {
  MediaDomain,
  MediaJob,
  MediaJobSummary,
  MediaJobsByEntity,
  MediaRole,
  UploadSession,
} from "src/types/media";

/**
 * Every media call in one place.
 *
 * Reads come from admin-api's own tables. Writes are proxied on to
 * klooma_db_api, which owns the path grammar and the transcoder - that is why
 * finishing an upload is a call to this app rather than to GCS.
 */

export interface MediaJobFilters {
  domain?: MediaDomain | "";
  role?: MediaRole | "";
  status?: string;
  lane?: string;
  stalled?: boolean;
  from?: string;
  to?: string;
  q?: string;
  page?: number;
  per_page?: number;
}

export interface Paginated<T> {
  data: T[];
  current_page: number;
  last_page: number;
  total: number;
}

const mediaService = {
  list(filters: MediaJobFilters = {}) {
    const params = Object.fromEntries(
      Object.entries(filters).filter(([, value]) => value !== "" && value != null)
    );

    return sendRequest
      .get<Paginated<MediaJob>>({ url: "media-jobs", data: params })
      .then((res) => res.data);
  },

  summary() {
    return sendRequest
      .get<MediaJobSummary>({ url: "media-jobs/summary" })
      .then((res) => res.data);
  },

  detail(ulid: string) {
    return sendRequest.get<MediaJob>({ url: `media-jobs/${ulid}` }).then((res) => res.data);
  },

  /**
   * Every job for a page of content, in one call.
   *
   * This is what replaced two queries per row against a table nothing wrote.
   * A list screen calls it once, after it has its rows.
   */
  forEntities(domain: MediaDomain, entityIds: number[], roles: MediaRole[] = ["main"]) {
    if (!entityIds.length) return Promise.resolve({} as MediaJobsByEntity);

    const query = [
      `domain=${domain}`,
      ...entityIds.map((id) => `entity_ids[]=${id}`),
      ...roles.map((role) => `roles[]=${role}`),
    ].join("&");

    return sendRequest
      .get<MediaJobsByEntity>({ url: `media-jobs/for?${query}` })
      .then((res) => res.data ?? {});
  },

  openUpload(payload: {
    domain: MediaDomain;
    entity_ids: number[];
    role: MediaRole;
    content_type: string;
    size_bytes: number;
  }) {
    return sendRequest
      .post<UploadSession>({ url: "media/uploads", data: payload })
      .then((res) => res.data);
  },

  finishUpload(
    uploadId: string,
    payload: { domain: MediaDomain; entity_ids: number[]; role: MediaRole }
  ) {
    return sendRequest
      .post<MediaJob>({ url: `media/uploads/${uploadId}/finish`, data: payload })
      .then((res) => res.data);
  },

  retry(ulid: string) {
    return sendRequest.post<MediaJob>({ url: `media-jobs/${ulid}/retry` }).then((r) => r.data);
  },

  cancel(ulid: string) {
    return sendRequest.post<MediaJob>({ url: `media-jobs/${ulid}/cancel` }).then((r) => r.data);
  },
};

export default mediaService;
